import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography
} from "@mui/material";
import {
    CacheProvider,
    EmotionCache
} from "@emotion/react";
import {
    useEffect,
    useState
} from "react";

import { StyleSheetManager } from "styled-components";
import createCache from '@emotion/cache';
import { createPortal } from "react-dom";

type ExternalWindowPopUpProps = {
    children: React.ReactNode;
    onClose: () => void;
};

function copyStyles(source: Document, destination: Document) {
    // Copy head elements from the original document to the new one
    source.head.childNodes.forEach((node) => {
        destination.head.appendChild(node.cloneNode(true));
    });

    // Copy inline styles from body to the new document
    const sourceBodyStyle = source.body.getAttribute("style");
    if (!sourceBodyStyle) {
        return;
    }
    destination.body.setAttribute("style", sourceBodyStyle);
    // When triggering pop-up from dialog, the body overflow is set to hidden. Reset it to auto
    destination.body.style.overflow = "auto";
}

export const ExternalWindowPopUp = ({ onClose, children }: ExternalWindowPopUpProps) => {
    const [portalRoot, setPortalRoot] = useState<HTMLDivElement | null>(null);
    const [externalWindow, setExternalWindow] = useState<Window | null>(null);
    const [emotionCache, setEmotionCache] = useState<EmotionCache | null>(null);
    const [showPopUpButton, setShowPopUpButton] = useState<boolean>(false);

    const cleanup = () => {
        onClose();
        externalWindow?.close();
        externalWindow?.removeEventListener("unload", cleanup);
        portalRoot?.remove();
        setPortalRoot(null);
        setExternalWindow(null);
        setEmotionCache(null);
    };

    useEffect(() => {
        setup();
        return () => {
            cleanup();
        };
    }, []);

    const setup = () => {
        const createdPortalRoot = document.createElement("div");
        setPortalRoot(createdPortalRoot);
        const createdExternalWindow = window.open("", "", "width=600, height=400, left=200, top=200, menubar=no, toolbar=no, location=no, status=no");
        createdExternalWindow?.addEventListener("unload", cleanup);
        if (!createdExternalWindow) {
            setShowPopUpButton(true);
            return;
        }
        setExternalWindow(createdExternalWindow);
        createdExternalWindow?.document.body.appendChild(createdPortalRoot);
        copyStyles(document, createdExternalWindow?.document!); // necessary for styles from css files (for instance, App.css and index.css)
        const createdEmotionCache = createCache({ key: "external-window", container: createdExternalWindow?.document.head });
        setEmotionCache(createdEmotionCache);
    }

    return (
        <>
            {
                portalRoot && externalWindow && emotionCache ? (
                    createPortal(
                        <CacheProvider value={emotionCache}>
                            <StyleSheetManager target={externalWindow?.document.head}>
                                {portalRoot && (
                                    <>
                                        {children}
                                    </>
                                )}
                            </StyleSheetManager>
                        </CacheProvider>,
                        portalRoot
                    )
                ) : <>
                </>
            }
            <Dialog open={showPopUpButton} onClose={() => setShowPopUpButton(false)}>
                <Box style={{ padding: "20px", textAlign: "center" }}>
                    <DialogTitle>Popup Blocked</DialogTitle>
                    <DialogContent>
                        <Typography>Some browsers disallow popups. Please click the button below to open the external window manually.</Typography>
                        <Typography>If the problem persists, try using another browser or device</Typography>
                    </DialogContent>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setup();
                            setShowPopUpButton(false);
                        }}
                    >
                        Open External Window
                    </Button>
                </Box>
            </Dialog>
        </>
    );
};